import { useCallback, useState, type ReactNode } from 'react';
import {
  FIELD_BOX_SCOPE_CLASS,
  type PlaceFieldCopy,
  type PlaceSuggestion,
} from '@voll-intelligence/intelligence-ui-kit';
import { BLOCK_SHELL } from '../../../shared/ui/block_shell.js';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { useMobilityPlacesPort } from '../../shared/mobility_places_port.js';
import type {
  ChatUiMobilityPlannerPlace,
  MobilityPlannerBlock,
  MobilityPlannerSearch,
  MobilityPlannerTripType,
} from '@voll-intelligence/genui-contract';
import {
  OutboundRow,
  ReturnRow,
  SearchButton,
  TripRow,
  type PlaceFieldShared,
} from './planner_fields.js';
import { PlannerRecord, type PlannerAnswers } from './planner_record.js';
import { initialWhen, liveWhen, searchWhen } from './planner_when.js';

type Place = ChatUiMobilityPlannerPlace;

/** A copy do painel de lugares, no catálogo da conversa (`mobilityPlanner.*`). */
function placesCopy(t: ReturnType<typeof useGenUiI18n>['t']): PlaceFieldCopy {
  return {
    label: t('mobilityPlanner.placeholder'),
    emptyLabel: t('mobilityPlanner.noPlaces'),
    errorLabel: t('mobilityPlanner.placesError'),
    retryLabel: t('mobilityPlanner.retry'),
    promptLabel: t('mobilityPlanner.prompt'),
  };
}

/** O campo da vez: o primeiro ainda sem lugar (escolhida a origem, o destino
 *  abre). `null` quando a ida já tem as pontas. */
function wantedField(
  ui: MobilityPlannerBlock,
  origin: Place | undefined,
  destination: Place | undefined,
): 'origin' | 'destination' | null {
  if (!origin) return 'origin';
  return ui.destination && !destination ? 'destination' : null;
}

/** A corrida de cooperativa: o bloco traz o Sentido (e, com ele, as Pessoas e a
 *  Volta). Sem destino (carro com motorista) não há sentido a perguntar. */
const hasTripControls = (ui: MobilityPlannerBlock): boolean =>
  ui.tripType !== undefined && ui.destination !== undefined;

/** A busca do Buscar, ou `null` enquanto falta lugar — na ida e volta, também as
 *  pontas da volta — ou, com o Quando editável, a data e a hora. */
function searchOf(ui: MobilityPlannerBlock, answers: PlannerAnswers): MobilityPlannerSearch | null {
  const { origin, destination, tripType, passengers, backOrigin, backDestination } = answers;
  const when = searchWhen(ui, answers.when);
  if (!origin || wantedField(ui, origin, destination) !== null || when === null) return null;
  const base = {
    action: 'mobility_planner_search' as const,
    origin,
    ...(ui.destination && destination ? { destination } : {}),
    ...(when ? { when } : {}),
  };
  if (!hasTripControls(ui)) return base;
  if (tripType === 'one_way') return { ...base, tripType, passengers };
  if (!backOrigin || !backDestination) return null;
  return {
    ...base,
    tripType,
    passengers,
    return: { origin: backOrigin, destination: backDestination },
  };
}

/** As respostas vivas do card: os lugares, o Sentido, as Pessoas, as pontas
 *  da volta e o Quando. A volta nasce invertida: o que a pessoa não trocou
 *  acompanha a ida. */
function usePlannerAnswers(ui: MobilityPlannerBlock) {
  const [origin, setOrigin] = useState<Place | undefined>(ui.origin.selected);
  const [destination, setDestination] = useState<Place | undefined>(ui.destination?.selected);
  const [tripType, setTripType] = useState<MobilityPlannerTripType>(ui.tripType ?? 'one_way');
  const [passengers, setPassengers] = useState(ui.passengers ?? 1);
  const [backOrigin, setBackOrigin] = useState<Place | undefined>(ui.return?.origin?.selected);
  const [backDestination, setBackDestination] = useState<Place | undefined>(
    ui.return?.destination?.selected,
  );
  const [when, setWhen] = useState(() => initialWhen(ui));
  const answers: PlannerAnswers = {
    origin,
    destination,
    tripType,
    passengers,
    backOrigin: backOrigin ?? destination,
    backDestination: backDestination ?? origin,
    when,
  };
  return {
    answers,
    setWhen,
    setOrigin,
    setDestination,
    setTripType,
    setPassengers,
    setBackOrigin,
    setBackDestination,
  };
}

/** A busca de lugares pelo port do host, como linhas do painel. Estável: é dep
 *  do effect de busca do `PlaceField`. */
function useSearchPlaces(): (term: string) => Promise<readonly PlaceSuggestion[]> {
  const port = useMobilityPlacesPort();
  return useCallback(
    async (term: string): Promise<readonly PlaceSuggestion[]> => {
      const places = port ? await port.searchPlaces(term) : [];
      return places.map((p) => ({
        id: p.ref,
        label: p.name,
        icon: 'place' as const,
        ...(p.address ? { description: p.address } : {}),
      }));
    },
    [port],
  );
}

/**
 * A escolha de origem e destino do agendamento de carro na conversa
 * (`mobility_planner`): o agente não teve certeza de um dos lugares, e a pessoa
 * escolhe entre as sugestões dele ou busca outro endereço. O Buscar devolve os
 * `ref`s (`onSearch`). Sem destino no bloco (carro com motorista), a origem
 * basta.
 *
 * Na corrida de cooperativa (`tripType` no bloco), o Sentido e as Pessoas vêm
 * ANTES da busca — mudam preço e veículo — e a ida e volta ganha a Volta em
 * linha própria: a rota nasce invertida (editável: o pouso em outro aeroporto)
 * e o horário é o que o agente sugeriu pelo voo da viagem, com a dica.
 *
 * Com `editableWhen`, o Para quando abre data e hora e o Buscar espera as duas.
 *
 * O campo que falta abre sozinho só no card que ainda é o último da conversa e
 * nunca no remontado do histórico; card congelado ou superado fica travado.
 */
export function MobilityPlannerCard({
  ui,
  note,
  readOnly = false,
  superseded = false,
  replay = false,
  onSearch,
}: Readonly<{
  ui: MobilityPlannerBlock;
  note?: string;
  readOnly?: boolean;
  superseded?: boolean;
  replay?: boolean;
  onSearch: (search: MobilityPlannerSearch) => void;
}>): ReactNode {
  const { t } = useGenUiI18n();
  const { renderMarkdown } = useGenUiConfig();
  const searchPlaces = useSearchPlaces();
  const state = usePlannerAnswers(ui);
  const { answers } = state;
  const locked = readOnly || superseded;
  const tripMode = hasTripControls(ui);
  const roundTrip = tripMode && answers.tripType === 'round_trip';
  const guided = !locked && !replay;
  const wanted = wantedField(ui, answers.origin, answers.destination);
  const search = searchOf(ui, answers);
  const when = liveWhen({
    ui,
    value: answers.when,
    placesReady: wanted === null,
    missingText: {
      both: t('mobilityPlanner.whenMissing'),
      date: t('mobilityPlanner.whenMissingDate'),
      time: t('mobilityPlanner.whenMissingTime'),
    },
    onChange: state.setWhen,
  });
  const emptyText = t('mobilityPlanner.placeholder');
  const shared: PlaceFieldShared = {
    copy: placesCopy(t),
    suggestionsLabel: t('mobilityPlanner.suggestions'),
    emptyText,
    onSearch: searchPlaces,
  };
  const searchButton = (
    <SearchButton
      label={t('mobilityPlanner.submit')}
      ready={search !== null}
      missing={when.missing}
      onSearch={() => {
        if (search) onSearch(search);
      }}
    />
  );
  return (
    <div className="genui2 grid gap-3">
      {note && <div className="itin-intro">{renderMarkdown ? renderMarkdown(note) : note}</div>}
      {/* A casca do bloco (#6321) é o `@container` da fileira: as pontas dividem o
          que sobra, o Quando mede 180 e o Buscar fecha a linha; na coluna estreita
          (celular) a fileira empilha e o Buscar ocupa a largura toda. Travado, o
          card vira registro: as respostas, sem nenhum controle. */}
      <div className={`${BLOCK_SHELL} @container`}>
        {locked ? (
          <PlannerRecord ui={ui} answers={answers} tripMode={tripMode} emptyText={emptyText} />
        ) : (
          <div className={`grid gap-3 ${FIELD_BOX_SCOPE_CLASS}`}>
            {tripMode && (
              <TripRow
                who={ui.who}
                tripType={answers.tripType}
                passengers={answers.passengers}
                onTripType={state.setTripType}
                onPassengers={state.setPassengers}
              />
            )}
            <OutboundRow
              ui={ui}
              shared={shared}
              origin={answers.origin}
              destination={answers.destination}
              openField={guided ? wanted : null}
              tripMode={tripMode}
              roundTrip={roundTrip}
              onOrigin={state.setOrigin}
              onDestination={state.setDestination}
              whenField={when.field}
              searchButton={roundTrip ? null : searchButton}
            />
            {roundTrip && (
              <ReturnRow
                ui={ui}
                shared={shared}
                backOrigin={answers.backOrigin}
                backDestination={answers.backDestination}
                onBackOrigin={state.setBackOrigin}
                onBackDestination={state.setBackDestination}
                searchButton={searchButton}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
